import { useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  Case,
  CasePage,
  TimeEntry,
  TimeEntryPage,
  TimeReportResponse,
  User,
} from "../lib/api";
import { useAuth } from "../lib/auth";

function formatMinutes(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthAgoISO(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}

type Tab = "entries" | "report";

export default function AdminTimeEntries() {
  const me = useAuth((s) => s.user);
  const isAdmin = me?.role === "owner" || me?.role === "admin";

  const [tab, setTab] = useState<Tab>("entries");

  // --------------- entries tab ---------------
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [filterUser, setFilterUser] = useState<number | "">("");
  const [filterDateFrom, setFilterDateFrom] = useState(monthAgoISO());
  const [filterDateTo, setFilterDateTo] = useState(todayISO());
  const [offset, setOffset] = useState(0);
  const pageSize = 50;

  const [users, setUsers] = useState<User[]>([]);
  const [cases, setCases] = useState<Case[]>([]);

  // --------------- report tab ---------------
  const [report, setReport] = useState<TimeReportResponse | null>(null);
  const [reportUser, setReportUser] = useState<number | "">("");
  const [reportFrom, setReportFrom] = useState(monthAgoISO());
  const [reportTo, setReportTo] = useState(todayISO());
  const [reportLoading, setReportLoading] = useState(false);

  // --------------- editing ---------------
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editHours, setEditHours] = useState("");
  const [editMins, setEditMins] = useState("");
  const [editMileage, setEditMileage] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editCase, setEditCase] = useState<number | "">("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    void loadUsers();
    void loadCases();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadEntries();
  }, [isAdmin, filterUser, filterDateFrom, filterDateTo, offset]);

  async function loadUsers() {
    try {
      const list = await api.get<User[]>("/users");
      setUsers(list);
    } catch { /* non-critical */ }
  }

  async function loadCases() {
    try {
      // The case-name lookup table needs every case the admin can see.
      const page = await api.get<CasePage>("/cases?limit=500");
      setCases(page.items);
    } catch { /* non-critical */ }
  }

  async function loadEntries() {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (filterUser) params.set("user_id", String(filterUser));
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      params.set("limit", String(pageSize));
      params.set("offset", String(offset));
      const page = await api.get<TimeEntryPage>(`/time-entries?${params}`);
      setEntries(page.items);
      setTotal(page.total);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load entries");
    } finally {
      setLoading(false);
    }
  }

  async function loadReport() {
    setReportLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        date_from: reportFrom,
        date_to: reportTo,
      });
      if (reportUser) params.set("user_id", String(reportUser));
      const r = await api.get<TimeReportResponse>(`/time-entries/report?${params}`);
      setReport(r);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load report");
    } finally {
      setReportLoading(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 3500);
  }

  function openEdit(e: TimeEntry) {
    setEditingEntry(e);
    setEditDate(e.entry_date);
    setEditHours(String(Math.floor(e.minutes / 60)));
    setEditMins(String(e.minutes % 60));
    setEditMileage(e.mileage != null ? String(e.mileage) : "");
    setEditNote(e.note ?? "");
    setEditCase(e.case_id ?? "");
  }

  function closeEdit() {
    setEditingEntry(null);
  }

  async function saveEdit() {
    if (!editingEntry) return;
    const totalMinutes = (parseInt(editHours || "0", 10) * 60) + parseInt(editMins || "0", 10);
    if (totalMinutes < 1) {
      setErr("Enter at least 1 minute.");
      return;
    }
    const body: Record<string, unknown> = {
      entry_date: editDate,
      minutes: totalMinutes,
      note: editNote.trim() || null,
      case_id: editCase || null,
    };
    if (editMileage.trim()) body.mileage = parseFloat(editMileage);

    setSaving(true);
    try {
      const updated = await api.patch<TimeEntry>(`/time-entries/${editingEntry.id}`, body);
      setEntries((cur) => cur.map((e) => (e.id === updated.id ? updated : e)));
      closeEdit();
      flashFor("Entry updated.");
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to update entry");
    } finally {
      setSaving(false);
    }
  }

  async function deleteEntry(id: number) {
    if (!confirm("Delete this time entry?")) return;
    try {
      await api.del(`/time-entries/${id}`);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      setTotal((n) => n - 1);
      flashFor("Entry deleted.");
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to delete entry");
    }
  }

  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;

  if (!isAdmin) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Time tracking</div>
            <h1>Time & mileage</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Time tracking management is restricted to organization owners and admins.
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Site settings</div>
          <h1>Time & mileage</h1>
          <p className="admin__lead">
            View and manage time entries and mileage logged by team members.
            Run reports to see totals over any date range.
          </p>
        </div>
      </header>

      <div className="tabs">
        <button
          type="button"
          className={tab === "entries" ? "active" : ""}
          onClick={() => setTab("entries")}
        >
          Entries
        </button>
        <button
          type="button"
          className={tab === "report" ? "active" : ""}
          onClick={() => setTab("report")}
        >
          Reports
        </button>
      </div>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      {/* ==================== ENTRIES TAB ==================== */}
      {tab === "entries" && (
        <>
          <div className="te-filters">
            <label className="te-filters__field">
              <span>User</span>
              <select
                value={filterUser}
                onChange={(e) => { setFilterUser(e.target.value ? Number(e.target.value) : ""); setOffset(0); }}
              >
                <option value="">All users</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </label>
            <label className="te-filters__field">
              <span>From</span>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => { setFilterDateFrom(e.target.value); setOffset(0); }}
              />
            </label>
            <label className="te-filters__field">
              <span>To</span>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => { setFilterDateTo(e.target.value); setOffset(0); }}
              />
            </label>
          </div>

          <div className="admin__scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Time</th>
                  <th>Miles</th>
                  <th>Note</th>
                  <th>Case</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="muted">Loading…</td></tr>
                )}
                {!loading && entries.length === 0 && (
                  <tr><td colSpan={7} className="muted">No time entries found.</td></tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>{e.entry_date}</td>
                    <td>
                      <div className="admin-table__name">{e.user_display_name}</div>
                    </td>
                    <td>{formatMinutes(e.minutes)}</td>
                    <td>{e.mileage ?? "—"}</td>
                    <td className="te-recent__note">{e.note || "—"}</td>
                    <td>{e.case_name || "—"}</td>
                    <td className="admin-table__actions">
                      <button type="button" className="btn btn--sm" onClick={() => openEdit(e)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger-text"
                        onClick={() => deleteEntry(e.id)}
                      >
                        Del
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="te-pagination">
              <button
                type="button"
                className="btn btn--sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
              >
                ← Prev
              </button>
              <span className="muted">
                Page {currentPage} of {totalPages} ({total} entries)
              </span>
              <button
                type="button"
                className="btn btn--sm"
                disabled={currentPage >= totalPages}
                onClick={() => setOffset(offset + pageSize)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* ==================== REPORT TAB ==================== */}
      {tab === "report" && (
        <>
          <div className="te-filters">
            <label className="te-filters__field">
              <span>User</span>
              <select
                value={reportUser}
                onChange={(e) => setReportUser(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">All users</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </label>
            <label className="te-filters__field">
              <span>From</span>
              <input
                type="date"
                value={reportFrom}
                onChange={(e) => setReportFrom(e.target.value)}
              />
            </label>
            <label className="te-filters__field">
              <span>To</span>
              <input
                type="date"
                value={reportTo}
                onChange={(e) => setReportTo(e.target.value)}
              />
            </label>
            <div className="te-filters__field te-filters__field--action">
              <span>&nbsp;</span>
              <button
                type="button"
                className="btn btn--primary"
                onClick={loadReport}
                disabled={reportLoading}
              >
                {reportLoading ? "Loading…" : "Run report"}
              </button>
            </div>
          </div>

          {report && (
            <div className="te-report">
              <div className="te-report__summary">
                <div className="te-report__stat">
                  <span className="te-report__stat-label">Period</span>
                  <span className="te-report__stat-value">
                    {report.period_start} — {report.period_end}
                  </span>
                </div>
                <div className="te-report__stat">
                  <span className="te-report__stat-label">Total time</span>
                  <span className="te-report__stat-value">
                    {formatMinutes(report.total_minutes)}
                  </span>
                </div>
                <div className="te-report__stat">
                  <span className="te-report__stat-label">Total mileage</span>
                  <span className="te-report__stat-value">{report.total_mileage}</span>
                </div>
              </div>

              <div className="admin__scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Entries</th>
                      <th>Total time</th>
                      <th>Total miles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.length === 0 && (
                      <tr><td colSpan={5} className="muted">No entries in this period.</td></tr>
                    )}
                    {report.rows.map((r) => (
                      <tr key={r.user_id}>
                        <td className="admin-table__name">{r.user_display_name}</td>
                        <td className="admin-table__mono">{r.user_email}</td>
                        <td>{r.entry_count}</td>
                        <td>{formatMinutes(r.total_minutes)}</td>
                        <td>{r.total_mileage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== EDIT MODAL ==================== */}
      {editingEntry && (
        <div
          className="admin-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}
        >
          <form
            className="admin-modal"
            onSubmit={(ev) => { ev.preventDefault(); void saveEdit(); }}
          >
            <header className="admin-modal__head">
              <h2>Edit time entry</h2>
              <button type="button" className="admin-modal__close" onClick={closeEdit} aria-label="Close">×</button>
            </header>

            <div className="admin-card__grid">
              <div className="admin-card__field">
                <label htmlFor="te-date">Date</label>
                <input
                  id="te-date"
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  required
                />
              </div>
              <div className="admin-card__field">
                <label htmlFor="te-hours">Hours</label>
                <input
                  id="te-hours"
                  type="number"
                  min="0"
                  max="24"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                />
              </div>
              <div className="admin-card__field">
                <label htmlFor="te-mins">Minutes</label>
                <input
                  id="te-mins"
                  type="number"
                  min="0"
                  max="59"
                  value={editMins}
                  onChange={(e) => setEditMins(e.target.value)}
                />
              </div>
              <div className="admin-card__field">
                <label htmlFor="te-miles">Mileage</label>
                <input
                  id="te-miles"
                  type="number"
                  min="0"
                  step="0.1"
                  value={editMileage}
                  onChange={(e) => setEditMileage(e.target.value)}
                />
              </div>
              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="te-case">Case</label>
                <select
                  id="te-case"
                  value={editCase}
                  onChange={(e) => setEditCase(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">— none —</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="te-note">Note</label>
                <textarea
                  id="te-note"
                  rows={3}
                  maxLength={2000}
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </div>
            </div>

            <footer className="admin-modal__foot">
              <button type="button" className="btn" onClick={closeEdit} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
